import { useState } from "react";
import React from "react";
import { useApp, apiClient } from "@/App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Trophy, Check, ArrowRight, Clock, AlertTriangle } from "lucide-react";

const PinInput = ({ value, onChange, onSubmit }) => {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    onChange(raw);
    if (raw.length === 7 && /^[A-Z]{3}[0-9]{4}$/.test(raw)) {
      onSubmit(raw);
    }
  };

  const isValid = value.length === 0 || /^[A-Z]{0,3}[0-9]{0,4}$/.test(value);

  return (
    <div className="space-y-4" data-testid="pin-input">
      <div className="flex justify-center gap-1 mb-2">
        {[0, 1, 2].map((i) => (
          <div key={`l-${i}`} className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-all ${value[i] ? "border-primary bg-primary/10 text-primary" : "border-border bg-white"}`}>
            {value[i] || ""}
          </div>
        ))}
        <div className="w-2" />
        {[3, 4, 5, 6].map((i) => (
          <div key={`n-${i}`} className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-all ${value[i] ? "border-primary bg-primary/10 text-primary" : "border-border bg-white"}`}>
            {value[i] || ""}
          </div>
        ))}
      </div>
      <p className="text-xs text-center text-muted-foreground">Format: 3 letters + 4 numbers (e.g., ABC1234)</p>
      <input
        ref={inputRef}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        maxLength={7}
        value={value}
        onChange={handleChange}
        placeholder="Enter PIN"
        className={`w-full text-center text-lg font-mono tracking-widest py-3 rounded-lg border-2 outline-none transition-all ${!isValid ? 'border-red-400 bg-red-50' : 'border-border focus:border-primary'}`}
        data-testid="pin-text-input"
      />
    </div>
  );
};

export default function EnterResultPage() {
  const { fixtures, pendingResults, refreshData, selectedSeason } = useApp();
  const [step, setStep] = useState(1);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [pin, setPin] = useState("");
  const [verifiedClub, setVerifiedClub] = useState(null);
  const [isAdminEntry, setIsAdminEntry] = useState(false);
  const [adminEntryClub, setAdminEntryClub] = useState("home");
  const [homeScore, setHomeScore] = useState("");
  const [awayScore, setAwayScore] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resultStatus, setResultStatus] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Get fixtures that can have results entered
  // Only show matches on or before today that don't have a confirmed result
  const getAvailableFixtures = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    return fixtures.filter(f => {
      if (f.is_completed && !f.has_conflict) return false;
      if (!f.match_date) return false;
      const matchDate = new Date(f.match_date);
      if (matchDate > today) return false;
      return true;
    });
  };

  const availableFixtures = getAvailableFixtures();

  // Check if fixture has pending entry
  const getPendingEntry = (fixtureId) => {
    return pendingResults.find(p => p.fixture_id === fixtureId);
  };

  const handleSelectFixture = (fixtureId) => {
    const fixture = fixtures.find(f => f.id === fixtureId);
    setSelectedFixture(fixture);
    setStep(2);
  };

  const handleVerifyPin = async (pinValue) => {
    const submitPin = pinValue || pin;
    if (!selectedFixture || submitPin.length !== 7) return;

    try {
      // Try verifying against the two clubs in this fixture
      const clubs = [selectedFixture.home_club_id, selectedFixture.away_club_id];
      let verified = false;
      let clubName = "";
      let isAdmin = false;

      for (const clubId of clubs) {
        try {
          const response = await apiClient.post(`/clubs/${clubId}/verify-pin`, { pin: submitPin });
          if (response.data.verified) {
            verified = true;
            clubName = response.data.club_name;
            break;
          }
        } catch (e) {
          // Continue to next club
        }
      }

      // If not a club PIN, check if it's an admin PIN
      if (!verified) {
        try {
          const response = await apiClient.post('/admin/verify-pin', { pin: submitPin });
          if (response.data.verified) {
            verified = true;
            isAdmin = true;
            clubName = `Admin (${response.data.admin_type === 'super_admin' ? 'Super' : 'Standard'})`;
          }
        } catch (e) {
          // Not an admin PIN either
        }
      }

      if (verified) {
        setVerifiedClub(clubName);
        setIsAdminEntry(isAdmin);
        setStep(3);
        toast.success(`Verified as ${clubName}`);
      } else {
        toast.error("Invalid PIN — only the two clubs in this fixture or an admin can enter results");
        setPin("");
      }
    } catch (error) {
      toast.error("Verification failed. Please try again.");
      setPin("");
    }
  };

  const handleSubmitResult = async () => {
    if (!selectedFixture || homeScore === "" || awayScore === "") {
      toast.error("Please enter both scores");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        fixture_id: selectedFixture.id,
        home_score: parseFloat(homeScore),
        away_score: parseFloat(awayScore),
        club_pin: pin
      };
      if (isAdminEntry) {
        payload.enter_as_club_id = adminEntryClub === "away" ? selectedFixture.away_club_id : selectedFixture.home_club_id;
      }
      const response = await apiClient.post('/results', payload);

      setResultStatus(response.data);
      setShowSuccess(true);
      refreshData();
      
      setTimeout(() => {
        setShowSuccess(false);
        resetForm();
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit result");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedFixture(null);
    setPin("");
    setVerifiedClub(null);
    setIsAdminEntry(false);
    setAdminEntryClub("home");
    setHomeScore("");
    setAwayScore("");
    setResultStatus(null);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="enter-result-page">
      {/* Header */}
      <div className="bg-primary px-4 pt-8 pb-6">
        <h1 className="text-2xl font-extrabold text-white tracking-tight">Enter Result</h1>
      </div>

      <div className="px-4 py-6 pb-24">
        {/* Step 1: Select Fixture */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-select-fixture">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Double Entry Required</p>
                  <p className="text-xs mt-1">Results only appear after both teams enter matching scores.</p>
                </div>
              </div>
            </div>

            <h2 className="text-lg font-semibold">Select Match</h2>
            
            {availableFixtures.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Trophy size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-lg">All results up to date</p>
                <p className="text-sm mt-1">No fixtures currently require a result entry</p>
              </div>
            ) : (
              <div className="space-y-3">
                {availableFixtures.map((fixture) => {
                  const pending = getPendingEntry(fixture.id);
                  const hasConflict = fixture.has_conflict;
                  
                  return (
                    <button
                      key={fixture.id}
                      onClick={() => handleSelectFixture(fixture.id)}
                      className={`w-full match-card text-left transition-colors ${
                        pending ? 'border-amber-300 bg-amber-50' : 
                        'hover:border-primary'
                      }`}
                      data-testid={`fixture-select-${fixture.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-semibold">{fixture.home_club_name}</p>
                          <p className="text-sm text-muted-foreground">vs {fixture.away_club_name}</p>
                        </div>
                        <div className="text-right">
                          {pending ? (
                            <div className="text-right">
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                                <Clock size={12} />
                                Awaiting
                              </span>
                            </div>
                          ) : (
                            <ArrowRight size={20} className="text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Enter PIN */}
        {step === 2 && selectedFixture && (
          <div className="space-y-6" data-testid="step-enter-pin">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Lock size={28} className="text-primary" />
              </div>
              <h2 className="text-lg font-semibold">Enter Club PIN</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedFixture.home_club_name} vs {selectedFixture.away_club_name}
              </p>
            </div>

            <PinInput value={pin} onChange={setPin} onSubmit={handleVerifyPin} />

            <Button
              variant="ghost"
              onClick={resetForm}
              className="w-full"
              data-testid="back-btn"
            >
              Back to fixture selection
            </Button>
          </div>
        )}

        {/* Step 3: Enter Scores */}
        {step === 3 && selectedFixture && (
          <div className="space-y-6" data-testid="step-enter-scores">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#CCFF00] flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-primary" />
              </div>
              <h2 className="text-lg font-semibold">Enter Match Scores</h2>
              <p className="text-sm text-muted-foreground mt-1">Verified as {verifiedClub}</p>
            </div>

            {isAdminEntry && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800 font-semibold mb-2">Entering on behalf of:</p>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant={adminEntryClub === "home" ? "default" : "outline"}
                    onClick={() => setAdminEntryClub("home")}
                    className="flex-1"
                    data-testid="admin-entry-home"
                  >
                    {selectedFixture.home_club_name}
                  </Button>
                  <Button 
                    size="sm" 
                    variant={adminEntryClub === "away" ? "default" : "outline"}
                    onClick={() => setAdminEntryClub("away")}
                    className="flex-1"
                    data-testid="admin-entry-away"
                  >
                    {selectedFixture.away_club_name}
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg border border-border p-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <p className="font-semibold mb-2">{selectedFixture.home_club_name}</p>
                  <p className="text-xs text-muted-foreground mb-3">Home</p>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={homeScore}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?5?$/.test(v)) setHomeScore(v);
                    }}
                    className="w-full text-center text-3xl font-bold h-16 rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="-"
                    data-testid="home-score-input"
                  />
                </div>

                <div className="text-center">
                  <p className="font-semibold mb-2">{selectedFixture.away_club_name}</p>
                  <p className="text-xs text-muted-foreground mb-3">Away</p>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={awayScore}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?5?$/.test(v)) setAwayScore(v);
                    }}
                    className="w-full text-center text-3xl font-bold h-16 rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="-"
                    data-testid="away-score-input"
                  />
                </div>
              </div>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Points: Win = 2, Draw = 1, Loss = 0
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> The other team must also enter the result. Only matching results will be confirmed.
              </p>
            </div>

            <Button
              onClick={handleSubmitResult}
              disabled={submitting || homeScore === "" || awayScore === ""}
              className="w-full h-12 bg-primary hover:bg-primary/90"
              data-testid="submit-result-btn"
            >
              {submitting ? "Submitting..." : "Submit Result"}
            </Button>

            <Button
              variant="ghost"
              onClick={resetForm}
              className="w-full"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="max-w-sm">
          <div className="text-center py-6">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
              resultStatus?.status === 'confirmed' ? 'bg-[#CCFF00]' : 'bg-amber-100'
            }`}>
              {resultStatus?.status === 'confirmed' ? (
                <Check size={40} className="text-primary" />
              ) : (
                <Clock size={40} className="text-amber-600" />
              )}
            </div>
            <DialogHeader>
              <DialogTitle className="text-center text-xl">
                {resultStatus?.status === 'confirmed' ? 'Result Confirmed!' : 'Result Submitted'}
              </DialogTitle>
              <DialogDescription className="text-center">
                {resultStatus?.status === 'confirmed' 
                  ? resultStatus?.message 
                  : 'Your result has been recorded. Waiting for the other team to enter their score.'}
              </DialogDescription>
            </DialogHeader>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
